import classNames from "./SidePanel.module.css";
import adhocClassNames from "./AdhocSidePanel.module.css";
import Loader from "../common/Loader";
import { useRoom } from "../context/room/room";
import { LiveKitRoom } from "@livekit/components-react";
import { useAxios } from "../../hooks/axios";
import { usePublisherToken } from "../../hooks/token";
import { Translator } from "./SidePanel";
import { useTeamsContext } from "../../hooks/teams";
import { useContext, useEffect, useState } from "react";
import useUser from "../../hooks/user";
import { Image } from "@fluentui/react-components";
import { TeamsFxContext } from "./TeamsFxContext";

type MediaPermissionState = "granted" | "denied" | "prompt" | "loading";

function useMediaPermission(): MediaPermissionState {
  const [state, setState] = useState<MediaPermissionState>("loading");

  useEffect(() => {
    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((result) => {
        setState(result.state as MediaPermissionState);
        result.onchange = () => {
          setState(result.state as MediaPermissionState);
        };
      })
      .catch(() => {
        // Permissions API not supported; fall back to getUserMedia probe
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then((stream) => {
            stream.getTracks().forEach((t) => t.stop());
            setState("granted");
          })
          .catch(() => setState("denied"));
      });
  }, []);

  return state;
}

function MediaPermissionError() {
  return (
    <div className={adhocClassNames.PermissionError}>
      <h1>Microphone access required</h1>
      <h2>Search for and open the Langfinity Teams app</h2>
      <Image src={"media-permissions-denied/screenshot-1.png"} />
      <Image src={"media-permissions-denied/screenshot-2.png"} />
      <Image src={"media-permissions-denied/screenshot-3.png"} />
      <h2>In the top right corner of the app, click "..." then "App permissions"</h2>
      <Image src={"media-permissions-denied/screenshot-4.png"} />
      <h2>Enable media permissions</h2>
      <Image src={"media-permissions-denied/screenshot-5.png"} />
    </div>
  );
}

function AdhocSidePanel() {
  const { room, organization, loading } = useRoom();

  const axios = useAxios();
  const { token } = usePublisherToken(axios);

  const [user] = useUser();
  const teamsContext = useTeamsContext();
  const { teamsUserCredential } = useContext(TeamsFxContext);
  const mediaPermission = useMediaPermission();
  const [credentialDisplayName, setCredentialDisplayName] = useState<string | undefined>();

  useEffect(() => {
    teamsUserCredential?.getUserInfo().then((info) => {
      setCredentialDisplayName(info.displayName);
    }).catch(() => undefined);
  }, [teamsUserCredential]);

  if (!room || !organization || loading || mediaPermission === "loading") {
    return <Loader />;
  }

  if (mediaPermission === "denied") {
    return (
      <div className={classNames.SidePanel}>
        <MediaPermissionError />
      </div>
    );
  }

  return (
    <div className={classNames.SidePanel}>
      <LiveKitRoom
        audio={true}
        video={false}
        connect={true}
        token={token}
        connectOptions={{ autoSubscribe: false }}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
      >
        <Translator
          room={room}
          organization={organization}
          participantType="speaker"
          participantName={teamsContext?.user?.displayName || credentialDisplayName || user.auth?.name || "Guest"}
        />
      </LiveKitRoom>
    </div>
  );
}

export default AdhocSidePanel;
